import { useEffect, useRef, useState } from "react";
import { AppLayout, PageHeader } from "@/components/Layout";
import { useGameAuth } from "@/context/GameAuthContext";
import { GameAuth } from "@/components/game/GameAuth";
import { PartyPopper } from "lucide-react";
import { HorseCard } from "@/components/game/HorseCard";
import { TrainingDay } from "@/components/game/TrainingDay";
import { FeedRoom } from "@/components/game/FeedRoom";
import { GameNav } from "@/components/game/GameNav";
import { HorseProfile, GameInventory, GameJournal, ComingSoon, GameHome, MyStable, AlumniBook, AlumniProfile, AchievementsView, PremiumTeaser, DisciplineCompetitions } from "@/components/game/GameViews";
import { EventPopup } from "@/components/game/EventPopup";
import { Competition, AuctionReveal } from "@/components/game/Competition";
import { StableCompete } from "@/components/game/StableCompete";
import { Leaderboard } from "@/components/game/GameSocial";
import { PlayerProfileView, ProfileEditor, FindPlayers } from "@/components/game/PlayerProfile";
import { FriendsPage } from "@/components/game/FriendsPage";
import { BarnsHub } from "@/components/game/BarnsPage";
import { HMAPage } from "@/components/game/HMAPage";
import { GameShop } from "@/components/game/GameShop";
import { TackRoom } from "@/components/game/TackRoom";
import { StallPage } from "@/components/game/StallPage";
import { StableUpgrades } from "@/components/game/StableUpgrades";
import { PremiumShop } from "@/components/game/PremiumShop";
import { GameAdmin } from "@/components/game/GameAdmin";
import { Forum } from "@/components/game/Forum";
import { CollectorsBook } from "@/components/game/CollectorsBook";
import { SkipAhead } from "@/components/game/SkipAhead";
import { HorseArt } from "@/components/game/HorseArt";
import { HorseSilhouette } from "@/components/game/HorseSilhouette";
import { gameApi } from "@/lib/api";
import { GamePopup } from "@/components/game/GamePopups";
import { CoinHistory } from "@/components/game/CoinHistory";
import { toast } from "sonner";
import { Gamepad2, Coins, LogOut, Sparkles, X, RotateCcw, Menu, RefreshCw, CalendarDays, Heart, Trophy } from "lucide-react";
import confetti from "canvas-confetti";

const celebrateLevelUp = (lv) => {
  if (!lv) return;
  if (lv.leveled_up) {
    const fire = (ratio, opts) => confetti({ ...opts, origin: { y: 0.6 }, particleCount: Math.floor(260 * ratio), zIndex: 9999,
      colors: ["#c8a24a", "#5a3e2b", "#e8d9b0", "#7a9e6f", "#b8763f"] });
    fire(0.3, { spread: 30, startVelocity: 55 });
    fire(0.35, { spread: 70 });
    fire(0.25, { spread: 110, decay: 0.91, scalar: 0.9 });
    const coats = (lv.coats_unlocked || []).length;
    let msg = `🎉 Level ${lv.new_level}! ${lv.levels_gained > 1 ? `${lv.levels_gained} levels · ` : ""}+${lv.currency_awarded}🪙`;
    if (coats) msg += ` · ${coats} new Piceance–East Douglas coat${coats > 1 ? "s" : ""}!`;
    if (lv.next_reward_coat && lv.next_reward_in) {
      msg += `\n🏆 Next reward: ${lv.next_reward_coat} at Level ${lv.next_reward_level} (${lv.next_reward_in} to go)`;
    }
    toast.success(msg, { duration: 6000, style: { whiteSpace: "pre-line" } });
  } else if (lv.awarded && lv.xp_gained) {
    toast.success(`+${lv.xp_gained} XP`, { duration: 1800 });
  }
};

const celebrateDiscovery = (d) => {
  if (!d || (!d.new_coat && !d.new_hma)) return;
  const fire = (ratio, opts) => confetti({ ...opts, origin: { y: 0.7 }, particleCount: Math.floor(220 * ratio), zIndex: 9999,
    colors: ["#c8a24a", "#5a3e2b", "#7a9e6f", "#e8d9b0", "#b8763f"] });
  fire(0.25, { spread: 26, startVelocity: 55 });
  fire(0.35, { spread: 60 });
  fire(0.2, { spread: 100, decay: 0.91, scalar: 0.8 });
  fire(0.2, { spread: 120, startVelocity: 25, decay: 0.92, scalar: 1.2 });
  if (d.new_coat && d.new_hma) toast.success(`✨ New discovery! ${d.new_coat} coat & your first ${d.new_hma} mustang!`);
  else if (d.new_coat) toast.success(`✨ New coat discovered: ${d.new_coat}! Added to your Collectors Book.`);
  else toast.success(`✨ New HMA discovered: your first ${d.new_hma} mustang!`);
};


const readNav = () => { try { return JSON.parse(localStorage.getItem("bmm_game_nav") || "{}"); } catch { return {}; } };

export default function Game() {
  const { player, loading, logout, setPlayer } = useGameAuth();
  const [save, setSave] = useState(null);
  const [catalog, setCatalog] = useState({});
  const [ready, setReady] = useState(false);
  const [busy, setBusy] = useState(false);
  const [naming, setNaming] = useState(null);   // candidate horse being named
  const [nameInput, setNameInput] = useState("");
  const [confirmReset, setConfirmReset] = useState(false);
  const [showCoinHistory, setShowCoinHistory] = useState(false);
  const [inventory, setInventory] = useState({});
  const [shopCatalog, setShopCatalog] = useState({});
  const [showShop, setShowShop] = useState(false);
  const [inventoryTab, setInventoryTab] = useState(() => readNav().inventoryTab || "all");
  const [gyDismissed, setGyDismissed] = useState(false);
  const [bgRewards, setBgRewards] = useState([]);
  const dismissBgRewards = () => { gameApi.post("/game/backgrounds/rewards/seen").catch(() => {}); setBgRewards([]); };
  const [deathNotices, setDeathNotices] = useState([]);
  const dismissDeath = (id) => { gameApi.post(`/game/deaths/${id}/seen`).catch(() => {}); setDeathNotices((n) => n.filter((d) => d.id !== id)); };
  const [prizeNotices, setPrizeNotices] = useState([]);
  const dismissPrize = (id) => { gameApi.post(`/game/prizes/${id}/seen`).catch(() => {}); setPrizeNotices((n) => n.filter((d) => d.id !== id)); };
  const [friendNotices, setFriendNotices] = useState([]);
  const closeFriendNotice = (id) => { gameApi.post(`/game/friends/notices/${id}/seen`).catch(() => {}); setFriendNotices((n) => n.filter((d) => d.id !== id)); };
  const actFriendNotice = (fn, id, msg) => fn.then(() => { toast.success(msg); }).catch((e) => toast.error(e.response?.data?.detail || "Failed")).finally(() => closeFriendNotice(id));
  const [view, setView] = useState(() => readNav().view || "play");
  const [profileTab, setProfileTab] = useState(() => readNav().profileTab || "training");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [eventPopup, setEventPopup] = useState(null);
  const [showSkip, setShowSkip] = useState(false);
  const [alumniSel, setAlumniSel] = useState(null);
  const [alumniUnread, setAlumniUnread] = useState(0);
  const [levelUpReward, setLevelUpReward] = useState(null);
  const [showResults, setShowResults] = useState([]);
  const [menuOrder, setMenuOrder] = useState(null);
  useEffect(() => {
    gameApi.get("/game/shows/notifications").then((r) => { if ((r.data.notifications || []).length) setShowResults(r.data.notifications); }).catch(() => {});
  }, []);
  useEffect(() => { gameApi.get("/game/menu-config").then((r) => setMenuOrder(r.data.order)).catch(() => {}); }, []);
  const handleLevel = (lv) => {
    celebrateLevelUp(lv);
    const coats = lv?.leveled_up ? (lv.coats_unlocked || []) : [];
    if (coats.length) setLevelUpReward({ level: lv.new_level, coats });
  };
  useEffect(() => { gameApi.get("/game/alumni").then(({ data }) => setAlumniUnread(data.unread_updates || 0)).catch(() => {}); }, [player?.makeovers_completed, view]);
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const sess = p.get("premium_session");
    const cancel = p.get("premium_cancel");
    if (sess || cancel) {
      setPremiumReturn({ session: sess || null, cancelled: !!cancel });
      setView("premium");
      window.history.replaceState({}, "", "/game");
    }
  }, []);

  const collectStable = async () => {
    try {
      const { data } = await gameApi.post("/game/stable/collect");
      if (data.already) toast("Already collected today — come back tomorrow!");
      else if (data.collected) { toast.success(`Collected ${data.collected}🪙 from your stable!`); setPlayer({ ...player, currency: data.player.currency }); }
      else toast("Keep a horse to earn stable income.");
      cheer(data);
    } catch (e) { toast.error("Could not collect earnings"); }
  };
  const [skipSummary, setSkipSummary] = useState(null);
  const [stableComp, setStableComp] = useState(null);
  const [stableCompResult, setStableCompResult] = useState(null);
  const [sellConfirm, setSellConfirm] = useState(null);
  const [saleResult, setSaleResult] = useState(null);
  const [profileNum, setProfileNum] = useState(() => readNav().profileNum ?? null);
  const [stallSel, setStallSel] = useState(null);
  const [premiumReturn, setPremiumReturn] = useState({ session: null, cancelled: false });
  const keptHorses = (player?.alumni || []).filter((a) => a.decision === "keep").map((a) => ({ id: a.id, name: a.name, color_hex: a.color_hex }));

  // Persist current page/selection so a browser refresh returns the player to the same
  // place instead of the home surface.
  useEffect(() => {
    try {
      localStorage.setItem("bmm_game_nav", JSON.stringify({ view, profileTab, inventoryTab, profileNum, alumniId: alumniSel?.id || null }));
    } catch (e) { /* ignore */ }
  }, [view, profileTab, inventoryTab, profileNum, alumniSel]);

  // On first load, rehydrate a detail view's selection (or fall back) so restored views render safely.
  const navRestoredRef = useRef(false);
  useEffect(() => {
    if (!ready || !player || navRestoredRef.current) return;
    navRestoredRef.current = true;
    const nav = readNav();
    if (view === "alumni-profile" && !alumniSel) {
      const found = (player.alumni || []).find((a) => a.id === nav.alumniId);
      if (found) setAlumniSel(found);
      else setView(save && save.status !== "selecting" ? "play" : "game-home");
    }
    if (view === "stall" && !stallSel) setView("stable");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, player]);

  const openStall = (stall) => { setStallSel(stall); setView("stall"); };
  const backToStable = () => { setStallSel(null); setView("stable"); };
  const assignStall = async (stall, horseId) => {
    try { const { data } = await gameApi.post("/game/stable/assign", { stall, horse_id: horseId }); if (data.player) setPlayer(data.player); toast.success("Horse moved into the stall"); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not assign"); }
    backToStable();
  };
  const setFavoriteHorse = async (h) => {
    try { const { data } = await gameApi.post("/game/profile/update", { favorite_horse_id: h.id }); if (data.player) setPlayer(data.player); else setPlayer({ ...player, favorite_horse_id: h.id }); toast.success(`${h.name} is now your favorite ⭐`); }
    catch (e) { toast.error("Could not set favorite"); }
    setStallSel((s) => (s && s.horse ? { ...s, horse: { ...s.horse, is_favorite: true } } : s));
  };

  const enterStable = async (al) => {
    if (!al?.id) { toast.error("Couldn't open that horse — please reload the game and try again."); return; }
    setBusy(true);
    try {
      const { data } = await gameApi.post("/game/stable/enter", { alumni_id: al.id });
      setSave(data.save); setView("play"); toast.success(`${al.name} is back in training!`);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not open training"); }
    finally { setBusy(false); }
  };

  const ensureStableForProfile = async (al) => {
    if (!al?.id) return;
    if (save?.status === "stable" && save?.stable_alumni_id === al.id) return;
    setBusy(true);
    try { const { data } = await gameApi.post("/game/stable/enter", { alumni_id: al.id }); setSave(data.save); toast.success(`${al.name} is ready for a training session!`); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not start session"); }
    finally { setBusy(false); }
  };

  const exitStable = async () => {
    setBusy(true);
    try {
      await gameApi.post("/game/stable/exit");
      const { data } = await gameApi.get("/game/state");
      if (data.player) setPlayer(data.player);
      setSave(data.save || null);
      if (data.save && data.save.status === "training") { setView("play"); toast.success("Back to your makeover in progress."); }
      else setView("stable");
    } catch (e) { toast.error("Could not finish the session"); }
    finally { setBusy(false); }
  };

  // SAFEGUARD: a kept-horse training session ("stable" overlay) must never strand the
  // in-progress makeover. It only lives while inside the kept horse's profile — the moment
  // the player is back on a home surface, restore the parked makeover so it can't disappear.
  const stableRestoreRef = useRef(false);
  useEffect(() => {
    if (save?.status === "stable" && !stableRestoreRef.current && ["game-home", "stable", "play"].includes(view)) {
      stableRestoreRef.current = true;
      exitStable().finally(() => { stableRestoreRef.current = false; });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [save?.status, view]);

  const sellStable = async (al) => {
    if (!al?.id) { toast.error("Couldn't sell that horse — please reload the game and try again."); return; }
    setBusy(true); setSellConfirm(null);
    try {
      const { data } = await gameApi.post("/game/stable/sell", { alumni_id: al.id });
      if (data.player) setPlayer(data.player);
      setSaleResult({ auction: data.auction, coins: data.sale_price, name: al.name });
      if (save?.status === "stable" && save?.stable_alumni_id === al.id) {
        const { data: st } = await gameApi.get("/game/state");
        if (st.player) setPlayer(st.player);
        setSave(st.save || null);
        setView(st.save && st.save.status === "training" ? "play" : "stable");
      }
      cheer(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not sell"); }
    finally { setBusy(false); }
  };

  const startStableCompete = async () => {
    setBusy(true);
    try {
      const { data } = await gameApi.post("/game/stable/compete/start");
      setStableComp(data.competition); setStableCompResult(null);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not start the competition"); }
    finally { setBusy(false); }
  };

  const submitStableCompete = async (perfs) => {
    setBusy(true);
    try {
      const { data } = await gameApi.post("/game/stable/compete/submit", perfs);
      setStableCompResult(data.results); setSave(data.save);
      if (data.player) setPlayer({ ...player, currency: data.player.currency });
      cheer(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not score the competition"); }
    finally { setBusy(false); }
  };

  const closeStableCompete = () => { setStableComp(null); setStableCompResult(null); };

  useEffect(() => {
    if (!player) return;
    Promise.all([gameApi.get("/game/state"), gameApi.get("/game/activities"), gameApi.get("/game/shop")])
      .then(([s, c, sh]) => {
        setSave(s.data.save); setCatalog(c.data); setShopCatalog(sh.data.shop); setInventory(sh.data.inventory || {});
        if (s.data.player) setPlayer(s.data.player);  // hydrate full player (alumni/achievements) — login returns a slim player
        if (s.data.game_year_new) { try { confetti({ particleCount: 140, spread: 75, origin: { y: 0.4 }, zIndex: 9999 }); } catch (e) {} }
        if (s.data.new_background_rewards?.length) { setBgRewards(s.data.new_background_rewards); try { confetti({ particleCount: 120, spread: 70, origin: { y: 0.4 }, zIndex: 9999 }); } catch (e) {} }
        if (s.data.death_notices?.length) setDeathNotices(s.data.death_notices);
        if (s.data.prize_notices?.length) { setPrizeNotices(s.data.prize_notices); try { confetti({ particleCount: 160, spread: 80, origin: { y: 0.4 }, zIndex: 9999 }); } catch (e) {} }
        gameApi.get("/game/friends/notices").then((fn) => { if (fn.data.notices?.length) setFriendNotices(fn.data.notices); }).catch(() => {});
      })
      .catch(() => {})
      .finally(() => setReady(true));
  }, [player?.id]);  // eslint-disable-line react-hooks/exhaustive-deps

  const newMakeover = async () => {
    setBusy(true);
    try { const { data } = await gameApi.post("/game/new-makeover"); setSave(data.save); setView("play"); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not start"); }
    finally { setBusy(false); }
  };

  const refreshPicks = async () => {
    const todayUTC = new Date().toISOString().slice(0, 10);
    const freeAvail = player?.last_free_reroll !== todayUTC;
    if (!freeAvail && (player?.currency || 0) < 50) { toast.error("You've used today's free refresh — next one costs 50 coins."); return; }
    setBusy(true);
    try {
      const { data } = await gameApi.post("/game/refresh-picks");
      setSave(data.save);
      if (data.player) setPlayer(data.player);
      toast.success(data.was_free ? "Free daily refresh — fresh mustangs rounded up! 🐴" : "Fresh mustangs rounded up! (−50🪙)");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not refresh picks"); }
    finally { setBusy(false); }
  };

  const openNaming = (horse) => { setNaming(horse); setNameInput(""); };

  const confirmChoose = async () => {
    const name = nameInput.trim();
    if (!name) { toast.error("Give your mustang a name first!"); return; }
    setBusy(true);
    try {
      const { data } = await gameApi.post("/game/choose", { horse_id: naming.id, name });
      setSave(data.save); setNaming(null); toast.success(`${name} is yours!`);
      celebrateDiscovery(data.discovery);
    }
    catch (e) { toast.error(e.response?.data?.detail || "Could not choose"); }
    finally { setBusy(false); }
  };

  const train = async (body) => {
    setBusy(true);
    try {
      const { data } = await gameApi.post("/game/train", body);
      setSave(data.save);
      handleLevel(data.level);
      if (data.currency_gain) setPlayer({ ...player, currency: player.currency + data.currency_gain });
    } catch (e) { toast.error(e.response?.data?.detail || "Training failed"); }
    finally { setBusy(false); }
  };

  const endDay = async () => {
    setBusy(true);
    try {
      const { data } = await gameApi.post("/game/endday");
      setSave(data.save);
      if (data.player) { setInventory(data.player.inventory || {}); setPlayer({ ...player, currency: data.player.currency }); }
      if (data.event) setEventPopup(data.event);
      cheer(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not end day"); }
    finally { setBusy(false); }
  };

  const resetProgress = async () => {
    setBusy(true);
    try {
      await gameApi.post("/game/reset");
      setSave(null); setConfirmReset(false);
      toast.success("Progress cleared. Start a fresh makeover!");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not reset"); }
    finally { setBusy(false); }
  };

  const care = async (action, itemId) => {
    setBusy(true);
    try {
      const { data } = await gameApi.post("/game/care", { action, item_id: itemId || null });
      setSave(data.save);
      handleLevel(data.level);
      if (data.player) { setInventory(data.player.inventory || {}); setPlayer({ ...player, currency: data.player.currency }); }
    } catch (e) { toast.error(e.response?.data?.detail || "Care failed"); }
    finally { setBusy(false); }
  };

  const buy = async (itemId) => {
    setBusy(true);
    try {
      const { data } = await gameApi.post("/game/shop/buy", { item_id: itemId, qty: 1 });
      setInventory(data.player.inventory || {}); setPlayer({ ...player, currency: data.player.currency });
      toast.success("Added to your feed room!");
    } catch (e) { toast.error(e.response?.data?.detail || "Purchase failed"); }
    finally { setBusy(false); }
  };

  const useItem = (itemId, category) => {
    const action = category === "feeds" ? "grain" : category === "supplements" ? "supplement" : "treat";
    setShowShop(false);
    care(action, itemId);
  };

  const cheer = (data) => {
    (data?.new_achievements || []).forEach((a) => toast.success(`🏅 Achievement unlocked: ${a.name}`));
  };

  const dismissComplete = async () => {
    try { await gameApi.post("/game/complete/dismiss"); } catch (e) { /* ignore */ }
    setSave(null);
  };
  const leaveComplete = async (fn) => { await dismissComplete(); fn(); };
  const openAlumni = (al) => { setAlumniSel(al); setView("alumni-profile"); };

  const startCompete = async () => {
    setBusy(true);
    try { const { data } = await gameApi.post("/game/compete/start"); setSave(data.save); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not start the competition"); }
    finally { setBusy(false); }
  };

  const submitCompete = async (perfs) => {
    setBusy(true);
    try {
      const { data } = await gameApi.post("/game/compete/submit", perfs);
      setSave(data.save);
      handleLevel(data.level);
      if (data.player) setPlayer({ ...player, currency: data.player.currency });
      cheer(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not score the competition"); }
    finally { setBusy(false); }
  };

  const decideCompete = async (decision) => {
    setBusy(true);
    try {
      const { data } = await gameApi.post("/game/compete/decide", { decision });
      setSave(data.save); setView("play");
      if (data.player) setPlayer(data.player);
      cheer(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not finalize"); }
    finally { setBusy(false); }
  };

  const doSkip = async () => {
    setBusy(true);
    try {
      const { data } = await gameApi.post("/game/skip");
      setSave(data.save);
      setSkipSummary(data.summary);
      cheer(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not skip ahead"); }
    finally { setBusy(false); }
  };

  if (loading) return <AppLayout><div className="p-10 text-center text-muted-foreground">Loading…</div></AppLayout>;

  // Pages a player can open at any time — they must NEVER require an active makeover horse.
  // Rendered before the makeover-flow branches so "selecting"/"no makeover" states can't block them.
  const renderStandaloneView = () => {
    const homeBack = () => setView(save && save.status !== "selecting" ? "play" : "game-home");
    if (view === "inventory") return <GameInventory shop={shopCatalog} inventory={inventory} player={player} initialTab={inventoryTab} onUse={useItem} onShopFeed={() => setShowShop(true)} onShopTack={() => setView("shop")} onBack={homeBack} />;
    if (view === "journal") return <GameJournal save={save} onBack={homeBack} />;
    if (view === "stable") return <MyStable player={player} save={save} onOpenCurrent={() => { setProfileTab("training"); setView("profile"); }} onBack={() => setView("game-home")} onNewMakeover={newMakeover} onOpen={openAlumni} onCollect={collectStable} onEnter={enterStable} onSell={(a) => setSellConfirm(a)} onOpenStall={openStall} onOpenTack={() => { setInventoryTab("tack"); setView("inventory"); }} onOpenFeed={() => { setInventoryTab("feeds"); setView("inventory"); }} onAlumni={() => setView("alumni")} onGameHome={() => setView("game-home")} onUpgrades={() => setView("upgrades")} />;
    if (view === "alumni") return <AlumniBook onBack={() => setView("stable")} onOpen={openAlumni} />;
    if (view === "alumni-profile") return <AlumniProfile alumni={alumniSel} keptHorses={keptHorses} playerNumber={player?.player_number} onBack={() => { if (save?.status === "stable") exitStable(); else setView("stable"); }} save={save} catalog={catalog} shop={shopCatalog} inventory={inventory} busy={busy} onTrain={train} onEndDay={endDay} onCare={care} onOpenShop={() => setShowShop(true)} onEnsureStable={ensureStableForProfile} onStableCompete={startStableCompete} onSaveUpdate={setSave} onRenamed={(name, pl) => { if (pl) setPlayer(pl); setAlumniSel((prev) => prev ? { ...prev, name } : prev); }} />;
    return null;
  };

  return (
    <AppLayout>
      <PageHeader subtitle="A Bluegrass Mustang Makeover Game" title={<span className="inline-flex flex-wrap items-center gap-2">120 Days to Makeover<span data-testid="beta-badge" className="rounded-md bg-secondary px-1.5 py-0.5 align-middle text-[10px] font-bold uppercase tracking-wider text-secondary-foreground">Beta</span></span>}
        action={player && (
          <div className="flex items-center gap-2">
            <button data-testid="game-menu-btn" onClick={() => setDrawerOpen(true)} className="flex items-center gap-1 rounded-full bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground">
              <Menu size={14} /> Menu
            </button>
            <button data-testid="game-logout" onClick={logout} className="flex items-center gap-1 rounded-full border border-border px-3 py-1.5 text-xs font-semibold text-muted-foreground">
              <LogOut size={13} /> Exit
            </button>
          </div>
        )} />

      {!player ? (
        <>
          <div className="mx-4 mt-2 flex items-start gap-3 rounded-2xl border border-border bg-card p-4 shadow-sm">
            <span className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-secondary text-white"><Gamepad2 size={20} /></span>
            <p className="text-sm text-foreground/80">Pick a wild mustang and train it over 120 in-game days for a fictional Makeover. Randomized horses, traits, and events every time. This game is <span className="font-bold">completely separate</span> from the real event — nothing here affects real competitors, scores, or votes.</p>
          </div>
          <GameAuth />
        </>
      ) : !ready ? (
        <div className="p-10 text-center text-muted-foreground">Loading your stable…</div>
      ) : (
        <>
          <div className="mx-4 mt-2 flex items-center justify-between rounded-xl border border-border bg-card px-4 py-2.5 shadow-sm">
            <span className="text-sm font-bold text-primary" data-testid="player-name">👋 {player.display_name}</span>
            <div className="flex items-center gap-2">
              {save && save.status !== "selecting" && save.status !== "stable" && (
                <button data-testid="reset-progress-btn" onClick={() => setConfirmReset(true)}
                  className="flex items-center gap-1 rounded-full border border-destructive/40 px-2.5 py-1 text-xs font-semibold text-destructive active:scale-95">
                  <RotateCcw size={13} /> Restart
                </button>
              )}
              {player.game_year != null && (
                <span className="flex items-center gap-1 rounded-full bg-secondary/15 px-2.5 py-1 text-sm font-bold text-secondary" data-testid="player-game-year" title="Current in-game year">
                  <CalendarDays size={14} /> Year {player.game_year}
                </span>
              )}
              <button data-testid="player-currency" onClick={() => setShowCoinHistory(true)}
                title="View your coin history"
                className="flex items-center gap-1 rounded-full bg-amber-100 px-2.5 py-1 text-sm font-bold text-amber-700 transition-colors hover:bg-amber-200 active:scale-95">
                <Coins size={14} /> {player.currency}
              </button>
            </div>
          </div>

          {view === "achievements" ? <AchievementsView onBack={() => setView(save && save.status !== "selecting" ? "play" : "game-home")} />
            : ["stable", "alumni", "alumni-profile", "inventory", "journal"].includes(view) ? renderStandaloneView()
            : save && (save.status === "training" || save.status === "retired" || save.status === "stable") ? (
            view === "inventory" ? <GameInventory shop={shopCatalog} inventory={inventory} player={player} initialTab={inventoryTab} onUse={useItem} onShopFeed={() => setShowShop(true)} onShopTack={() => setView("shop")} onBack={() => setView("play")} />
            : view === "journal" ? <GameJournal save={save} onBack={() => setView("play")} />
            : view === "game-home" ? <GameHome player={player} save={save} onNavigate={(id) => { if (id === "feedroom") { setInventoryTab("feeds"); setView("inventory"); } else setView(id); }} />
            : view === "stable" ? <MyStable player={player} save={save} onOpenCurrent={() => { setProfileTab("training"); setView("profile"); }} onBack={() => setView("game-home")} onNewMakeover={newMakeover} onOpen={openAlumni} onCollect={collectStable} onEnter={enterStable} onSell={(a) => setSellConfirm(a)} onOpenStall={openStall} onOpenTack={() => { setInventoryTab("tack"); setView("inventory"); }} onOpenFeed={() => { setInventoryTab("feeds"); setView("inventory"); }} onAlumni={() => setView("alumni")} onGameHome={() => setView("game-home")} onUpgrades={() => setView("upgrades")} />
            : view === "alumni" ? <AlumniBook onBack={() => setView("stable")} onOpen={openAlumni} />
            : view === "alumni-profile" ? <AlumniProfile alumni={alumniSel} keptHorses={keptHorses} playerNumber={player?.player_number} onBack={() => { if (save?.status === "stable") exitStable(); else setView("stable"); }} save={save} catalog={catalog} shop={shopCatalog} inventory={inventory} busy={busy} onTrain={train} onEndDay={endDay} onCare={care} onOpenShop={() => setShowShop(true)} onEnsureStable={ensureStableForProfile} onStableCompete={startStableCompete} onSaveUpdate={setSave} onRenamed={(name, pl) => { if (pl) setPlayer(pl); setAlumniSel((prev) => prev ? { ...prev, name } : prev); }} />
            : view === "achievements" ? <AchievementsView onBack={() => setView("play")} />
            : save.status === "retired" ? (
              <Competition save={save} busy={busy}
                onNewMakeover={() => leaveComplete(newMakeover)}
                onVisitStable={() => leaveComplete(() => setView("stable"))}
                onGameHome={() => leaveComplete(() => setView("game-home"))}
                onViewAlumni={() => { const al = save.outcome?.alumni; leaveComplete(() => (al ? openAlumni(al) : setView("alumni"))); }} />
            ) : save.status === "stable" ? (
              <MyStable player={player} save={save} onOpenCurrent={() => { setProfileTab("training"); setView("profile"); }} onBack={() => setView("game-home")} onNewMakeover={newMakeover} onOpen={openAlumni} onCollect={collectStable} onEnter={enterStable} onSell={(a) => setSellConfirm(a)} onOpenStall={openStall} onOpenTack={() => { setInventoryTab("tack"); setView("inventory"); }} onOpenFeed={() => { setInventoryTab("feeds"); setView("inventory"); }} onAlumni={() => setView("alumni")} onGameHome={() => setView("game-home")} onUpgrades={() => setView("upgrades")} />
            ) : <HorseProfile key={`profile-${profileTab}`} initialTab={profileTab} save={save} onBack={() => setView("game-home")} catalog={catalog} shop={shopCatalog} inventory={inventory} busy={busy} onTrain={train} onEndDay={endDay} onCare={care} onOpenShop={() => setShowShop(true)} onSkip={() => setShowSkip(true)} onRenamed={(name, pl) => { if (pl) setPlayer(pl); setSave((prev) => prev && prev.horse ? { ...prev, horse: { ...prev.horse, name } } : prev); }} />
          ) : save && ["ready_to_compete", "competing", "complete"].includes(save.status) ? (
            <Competition save={save} busy={busy} onStart={startCompete} onSubmit={submitCompete} onDecide={decideCompete} />
          ) : save && save.status === "selecting" && save.candidates?.length ? (
            <div className="px-4 pt-4">
              <h2 className="font-display text-xl font-bold text-primary">Choose your mustang</h2>
              <p className="mb-3 text-sm text-muted-foreground">10 wild mustangs need a trainer. Each has strengths and challenges — no horse is perfect!</p>
              {(() => {
                const todayUTC = new Date().toISOString().slice(0, 10);
                const freeAvail = player?.last_free_reroll !== todayUTC;
                const lowCoins = !freeAvail && (player?.currency || 0) < 50;
                return (
                  <button data-testid="refresh-picks-btn" onClick={refreshPicks} disabled={busy || lowCoins}
                    className="mb-3 flex w-full items-center justify-center gap-2 rounded-xl border-2 border-secondary/40 bg-secondary/10 py-2.5 text-sm font-bold text-secondary disabled:opacity-50 active:scale-[0.99]">
                    <RefreshCw size={15} /> Refresh Picks · {freeAvail ? "Free today" : "50🪙"}{lowCoins ? " (not enough coins)" : ""}
                  </button>
                );
              })()}
              <div className="grid grid-cols-1 gap-4">
                {save.candidates.map((h) => <HorseCard key={h.id} horse={h} onSelect={openNaming} busy={busy} />)}
              </div>
            </div>
          ) : (player?.alumni?.length || player?.makeovers_completed) ? (
            view === "inventory" ? <GameInventory shop={shopCatalog} inventory={inventory} player={player} initialTab={inventoryTab} onShopFeed={() => setShowShop(true)} onShopTack={() => setView("shop")} onBack={() => setView("game-home")} />
            : view === "stable" ? <MyStable player={player} save={save} onOpenCurrent={() => { setProfileTab("training"); setView("profile"); }} onBack={() => setView("game-home")} onNewMakeover={newMakeover} onOpen={openAlumni} onCollect={collectStable} onEnter={enterStable} onSell={(a) => setSellConfirm(a)} onOpenStall={openStall} onOpenTack={() => { setInventoryTab("tack"); setView("inventory"); }} onOpenFeed={() => { setInventoryTab("feeds"); setView("inventory"); }} onAlumni={() => setView("alumni")} onGameHome={() => setView("game-home")} onUpgrades={() => setView("upgrades")} />
            : view === "alumni" ? <AlumniBook onBack={() => setView("game-home")} onOpen={openAlumni} />
            : view === "alumni-profile" ? <AlumniProfile alumni={alumniSel} keptHorses={keptHorses} playerNumber={player?.player_number} onBack={() => { if (save?.status === "stable") exitStable(); else setView("stable"); }} save={save} catalog={catalog} shop={shopCatalog} inventory={inventory} busy={busy} onTrain={train} onEndDay={endDay} onCare={care} onOpenShop={() => setShowShop(true)} onEnsureStable={ensureStableForProfile} onStableCompete={startStableCompete} onSaveUpdate={setSave} onRenamed={(name, pl) => { if (pl) setPlayer(pl); setAlumniSel((prev) => prev ? { ...prev, name } : prev); }} />
            : (
              <div className="px-4 pt-8 text-center" data-testid="game-home-hub">
                <span className="mx-auto grid h-16 w-16 place-items-center rounded-full bg-secondary/15 text-secondary"><Sparkles size={28} /></span>
                <h2 className="mt-3 font-display text-2xl font-bold text-primary">Welcome back, {player.display_name}!</h2>
                <p className="mt-1 text-sm text-muted-foreground">No active makeover right now. What would you like to do?</p>
                <div className="mx-auto mt-5 max-w-xs space-y-2">
                  <button data-testid="home-new-makeover-btn" disabled={busy} onClick={newMakeover} className="h-12 w-full rounded-xl bg-primary font-bold text-primary-foreground disabled:opacity-60 active:scale-95">Start New Makeover</button>
                  <button data-testid="home-stable-btn" onClick={() => setView("stable")} className="h-11 w-full rounded-xl border border-border bg-card font-bold text-primary active:scale-95">Visit My Stable</button>
                  <button data-testid="home-alumni-btn" onClick={() => setView("alumni")} className="h-11 w-full rounded-xl border border-border bg-card font-bold text-primary active:scale-95">View Alumni Book</button>
                  <div className="text-left"><PremiumTeaser onOpen={() => setView("premium")} /></div>
                </div>
              </div>
            )
          ) : (
            <div className="px-4 pt-8 text-center" data-testid="game-start">
              <span className="mx-auto grid h-16 w-16 place-items-center rounded-full bg-secondary/15 text-secondary"><Sparkles size={28} /></span>
              <h2 className="mt-3 font-display text-2xl font-bold text-primary">Ready for a Makeover?</h2>
              <p className="mt-1 text-sm text-muted-foreground">We'll wrangle up 10 wild mustangs for you to choose from.</p>
              <button data-testid="start-makeover-btn" disabled={busy} onClick={newMakeover}
                className="mt-4 h-12 rounded-xl bg-primary px-8 font-bold text-primary-foreground disabled:opacity-60 active:scale-95">
                {busy ? "Rounding them up…" : "Start New Makeover"}
              </button>
            </div>
          )}
        </>
      )}

      {naming && (
        <div className="fixed inset-0 z-[70] flex items-end justify-center bg-black/50 p-4 sm:items-center" data-testid="name-modal">
          <div className="w-full max-w-sm rounded-2xl border border-border bg-card p-5 shadow-xl">
            <div className="mb-2 flex items-center justify-between">
              <h3 className="font-display text-xl font-bold text-primary">Name your mustang</h3>
              <button data-testid="name-modal-close" onClick={() => setNaming(null)} className="rounded-full p-1 text-muted-foreground hover:bg-muted"><X size={18} /></button>
            </div>
            <p className="text-sm text-muted-foreground">You chose <span className="font-bold text-primary">Tag #{naming.tag}</span> — a {naming.color.toLowerCase()} {naming.sex.toLowerCase()} from the {naming.hma} HMA. What will you call them?</p>
            <input data-testid="name-input" autoFocus value={nameInput} maxLength={30}
              onChange={(e) => setNameInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") confirmChoose(); }}
              placeholder="e.g. Outlaw, Willow, Biscuit…"
              className="mt-3 h-12 w-full rounded-xl border border-border bg-background px-4 outline-none focus:ring-2 focus:ring-secondary" />
            <button data-testid="confirm-name-btn" disabled={busy} onClick={confirmChoose}
              className="mt-3 h-12 w-full rounded-xl bg-secondary font-bold text-white disabled:opacity-60 active:scale-95">
              {busy ? "Saddling up…" : "Start 120 Days"}
            </button>
          </div>
        </div>
      )}

      {confirmReset && (
        <div className="fixed inset-0 z-[70] flex items-end justify-center bg-black/50 p-4 sm:items-center" data-testid="reset-modal">
          <div className="w-full max-w-sm rounded-2xl border border-border bg-card p-5 shadow-xl">
            <h3 className="font-display text-xl font-bold text-primary">Delete this makeover?</h3>
            <p className="mt-1 text-sm text-muted-foreground">This clears your current horse and all 120-day progress so you can start fresh. Your player account and coins stay. This can't be undone.</p>
            <div className="mt-4 flex gap-2">
              <button data-testid="reset-cancel-btn" onClick={() => setConfirmReset(false)}
                className="h-11 flex-1 rounded-xl border-2 border-border bg-card font-bold text-primary active:scale-95">
                Keep Playing
              </button>
              <button data-testid="reset-confirm-btn" disabled={busy} onClick={resetProgress}
                className="h-11 flex-1 rounded-xl bg-destructive font-bold text-white disabled:opacity-60 active:scale-95">
                {busy ? "Clearing…" : "Delete & Restart"}
              </button>
            </div>
          </div>
        </div>
      )}

      {player?.game_year_new && !gyDismissed && (
        <div className="fixed inset-0 z-[130] grid place-items-center bg-black/60 p-4" data-testid="game-year-banner" onClick={() => setGyDismissed(true)}>
          <div className="w-full max-w-sm rounded-2xl bg-card p-6 text-center shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <PartyPopper className="mx-auto text-secondary" size={34} />
            <p className="mt-2 font-display text-2xl font-bold text-primary">Happy New Game Year!</p>
            <p className="mt-1 text-sm text-muted-foreground">It's now <span className="font-bold text-secondary">Game Year {player.game_year}</span>. Every horse in the game just grew a year older. 🐴</p>
            <button data-testid="game-year-banner-dismiss" onClick={() => setGyDismissed(true)} className="mt-4 h-11 w-full rounded-xl bg-primary font-bold text-primary-foreground active:scale-95">Onward!</button>
          </div>
        </div>
      )}
      {bgRewards.length > 0 && (
        <div className="fixed inset-0 z-[131] grid place-items-center bg-black/60 p-4" data-testid="bg-reward-popup" onClick={dismissBgRewards}>
          <div className="w-full max-w-sm rounded-2xl bg-card p-6 text-center shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <PartyPopper className="mx-auto text-secondary" size={34} />
            <p className="mt-2 font-display text-2xl font-bold text-primary">You unlocked a new background!</p>
            <p className="mt-1 text-sm text-muted-foreground">{bgRewards.length === 1 ? "It's waiting in your inventory — equip it from any horse's profile. 🐴" : `${bgRewards.length} new backgrounds are waiting in your inventory. 🐴`}</p>
            <div className="mt-4 space-y-2">
              {bgRewards.map((r) => (
                <div key={r.bg_id} data-testid={`bg-reward-${r.bg_id}`} className="flex items-center gap-3 rounded-xl border border-border bg-background p-2 text-left">
                  {r.image_path ? <img src={r.image_path} alt="" className="h-11 w-16 shrink-0 rounded-lg object-cover" /> : <div className="grid h-11 w-16 shrink-0 place-items-center rounded-lg border border-dashed border-border text-[9px] text-muted-foreground">Coming soon</div>}
                  <div className="min-w-0"><p className="truncate text-sm font-bold text-primary">{r.name || "New background"}</p>{r.count > 1 && <p className="text-[11px] text-muted-foreground">×{r.count}</p>}</div>
                </div>
              ))}
            </div>
            <button data-testid="bg-reward-dismiss" onClick={dismissBgRewards} className="mt-4 h-11 w-full rounded-xl bg-primary font-bold text-primary-foreground active:scale-95">Awesome!</button>
          </div>
        </div>
      )}
      {friendNotices.length > 0 && (() => {
        const d = friendNotices[0];
        const isReq = d.kind === "friend_request";
        const isBarnInvite = d.kind === "barn_invite";
        return (
          <div className="fixed inset-0 z-[134] grid place-items-center bg-black/60 p-4" data-testid="friend-notice-popup">
            <div className="w-full max-w-sm rounded-2xl bg-card p-6 text-center shadow-2xl" onClick={(e) => e.stopPropagation()}>
              <div className="mx-auto mb-2 grid h-12 w-12 place-items-center rounded-full bg-secondary/15 text-lg font-bold text-secondary">{(d.from_name || "?")[0]}</div>
              <p className="font-display text-lg font-bold text-primary" data-testid="friend-notice-text">{d.text}</p>
              {isReq ? (
                <div className="mt-4 flex gap-2">
                  <button data-testid="friend-notice-accept" onClick={() => actFriendNotice(gameApi.post(`/game/friends/request/${d.request_id}/accept`), d.id, "Friend added!")} className="h-11 flex-1 rounded-xl bg-primary font-bold text-primary-foreground active:scale-95">Accept</button>
                  <button data-testid="friend-notice-decline" onClick={() => actFriendNotice(gameApi.post(`/game/friends/request/${d.request_id}/decline`), d.id, "Declined")} className="h-11 flex-1 rounded-xl border border-border font-bold text-muted-foreground active:scale-95">Decline</button>
                </div>
              ) : isBarnInvite ? (
                <div className="mt-4 flex gap-2">
                  <button data-testid="barn-invite-accept" onClick={() => { actFriendNotice(gameApi.post(`/game/barns/invites/${d.invite_id}/accept`), d.id, `Joined ${d.barn_name}!`); setView("barns"); }} className="h-11 flex-1 rounded-xl bg-primary font-bold text-primary-foreground active:scale-95">Accept</button>
                  <button data-testid="barn-invite-decline" onClick={() => actFriendNotice(gameApi.post(`/game/barns/invites/${d.invite_id}/decline`), d.id, "Declined")} className="h-11 flex-1 rounded-xl border border-border font-bold text-muted-foreground active:scale-95">Decline</button>
                </div>
              ) : (
                <button data-testid="friend-notice-ok" onClick={() => closeFriendNotice(d.id)} className="mt-4 h-11 w-full rounded-xl bg-primary font-bold text-primary-foreground active:scale-95">Nice!</button>
              )}
            </div>
          </div>
        );
      })()}
      {prizeNotices.length > 0 && (() => {
        const d = prizeNotices[0];
        const per = d.kind === "season" ? "Seasonal" : "Monthly";
        return (
          <div className="fixed inset-0 z-[133] grid place-items-center bg-black/70 p-4" data-testid="prize-notice-popup">
            <div className="w-full max-w-sm overflow-hidden rounded-2xl bg-card shadow-2xl" onClick={(e) => e.stopPropagation()}>
              <div className="relative bg-gradient-to-b from-amber-200/60 to-transparent px-6 pt-6 pb-2 text-center">
                <Trophy className="mx-auto text-amber-500" size={34} />
                <p className="mt-1 font-display text-2xl font-bold text-primary" data-testid="prize-notice-title">{per} Champion! 🏆</p>
                {d.horse_image
                  ? <img src={d.horse_image} alt={d.horse_name} className="mx-auto mt-2 h-28 w-28 rounded-2xl object-cover shadow-md" />
                  : <div className="mx-auto mt-2 grid h-28 w-28 place-items-center rounded-2xl border border-dashed border-border text-xs text-muted-foreground">🐴</div>}
              </div>
              <div className="px-6 pb-6 text-center">
                <p className="text-sm text-muted-foreground"><span className="font-bold text-primary">{d.horse_name}</span> topped the <span className="font-bold text-secondary">{d.discipline}</span> leaderboard{d.period_key ? ` (${d.period_key})` : ""}!</p>
                <p className="mt-2 flex items-center justify-center gap-1 text-lg font-bold text-amber-600"><Coins size={18} /> +{d.amount} coins</p>
                <button data-testid="prize-notice-dismiss" onClick={() => dismissPrize(d.id)}
                  className="mt-4 h-11 w-full rounded-xl bg-primary font-bold text-primary-foreground active:scale-95">Collect</button>
              </div>
            </div>
          </div>
        );
      })()}
      {deathNotices.length > 0 && (() => {
        const d = deathNotices[0];
        return (
          <div className="fixed inset-0 z-[132] grid place-items-center bg-black/70 p-4" data-testid="death-notice-popup">
            <div className="w-full max-w-sm overflow-hidden rounded-2xl bg-card shadow-2xl" onClick={(e) => e.stopPropagation()}>
              <div className="relative bg-gradient-to-b from-secondary/15 to-transparent px-6 pt-6 pb-2 text-center">
                {d.horse_image
                  ? <img src={d.horse_image} alt={d.horse_name} className="mx-auto h-36 w-36 rounded-2xl object-cover shadow-md" data-testid="death-notice-image" />
                  : <div className="mx-auto grid h-36 w-36 place-items-center rounded-2xl border border-dashed border-border text-xs text-muted-foreground">🐴</div>}
                <p className="mt-3 flex items-center justify-center gap-1.5 font-display text-2xl font-bold text-primary" data-testid="death-notice-name">Remembering {d.horse_name} <Heart size={18} className="fill-rose-400 text-rose-400" /></p>
              </div>
              <div className="px-6 pb-6 text-center">
                <p className="text-sm text-muted-foreground">{d.horse_name} passed away peacefully at <span className="font-bold text-secondary">{d.death_age} years old</span> after a long life by your side.</p>
                <p className="mt-2 text-sm text-muted-foreground">Every horse leaves hoofprints behind. {d.horse_name}'s journey, accomplishments, and memories will remain with you. 🌿</p>
                <p className="mt-3 text-[11px] text-muted-foreground/80">Passed in Game Year {d.death_game_year} · Old Age</p>
                <button data-testid="death-notice-dismiss" onClick={() => dismissDeath(d.id)}
                  className="mt-4 h-11 w-full rounded-xl bg-primary font-bold text-primary-foreground active:scale-95">Remember {d.horse_name}</button>
              </div>
            </div>
          </div>
        );
      })()}
      {showShop && (
        <FeedRoom shop={shopCatalog} inventory={inventory} currency={player?.currency || 0}
          onBuy={buy} onUse={useItem} horse={save?.horse} onClose={() => setShowShop(false)} busy={busy} />
      )}

      <GameNav open={drawerOpen} onClose={() => setDrawerOpen(false)} view={view} order={menuOrder} alumniUnread={alumniUnread} isAdmin={player?.role === "game_admin"} hasHorse={!!(save && save.status !== "selecting")} canNew={save?.status === "retired" || (!save && (player?.alumni?.length || player?.makeovers_completed))}        onExit={() => { setDrawerOpen(false); logout(); }}
        onNavigate={(id) => {
          const go = () => {
            if (id === "newgame") { newMakeover(); return; }
            if (id === "feedroom") { setInventoryTab("feeds"); setView("inventory"); return; }
            if (id === "care" || id === "play") { if (save?.status === "training") { setProfileTab("training"); setView("profile"); } else { setView("play"); } return; }
            if (id === "myprofile") { setProfileNum(player.player_number); setView("player-profile"); return; }
            setView(id);
          };
          if (save?.status === "retired") { leaveComplete(go); } else { go(); }
        }} />

      {showResults.length > 0 && (
        <div className="fixed inset-0 z-[97] flex items-center justify-center bg-black/60 p-4" data-testid="show-results-modal">
          <div className="w-full max-w-sm rounded-2xl border border-emerald-300 bg-card p-5">
            <p className="text-center font-display text-xl font-bold text-primary">🏆 Show Results</p>
            <div className="mt-3 max-h-80 space-y-2 overflow-y-auto">
              {showResults.map((n) => (
                <div key={n.id} data-testid="show-result-item" className="rounded-xl bg-muted/50 p-3 text-sm">
                  <p className="font-bold text-primary">{n.horse_name} — {n.placing === 1 ? "🥇 1st" : n.placing === 2 ? "🥈 2nd" : n.placing === 3 ? "🥉 3rd" : `${n.placing}th`} of {n.total}</p>
                  <p className="text-xs text-muted-foreground">{n.kind === "championship" ? "Championship" : "30-Min Show"} · {n.discipline} · +{n.coins}🪙 · +{n.points} pts</p>
                </div>
              ))}
            </div>
            <button data-testid="show-results-close" onClick={async () => { try { await gameApi.post("/game/shows/notifications/read"); } catch (e) {} setShowResults([]); }}
              className="mt-4 h-11 w-full rounded-xl bg-emerald-600 font-bold text-white">Got it</button>
          </div>
        </div>
      )}
      <EventPopup event={eventPopup} onClose={() => setEventPopup(null)} />

      {levelUpReward && (
        <div className="fixed inset-0 z-[96] flex items-center justify-center bg-black/60 p-4" data-testid="levelup-reward-modal">
          <div className="w-full max-w-sm rounded-2xl border border-emerald-300 bg-card p-5 text-center shadow-xl">
            <p className="text-xs font-bold uppercase tracking-wide text-emerald-600">Level {levelUpReward.level} reward!</p>
            <h3 className="mt-1 font-display text-xl font-bold text-primary">New Piceance–East Douglas coat{levelUpReward.coats.length > 1 ? "s" : ""} unlocked</h3>
            <div className="mt-4 flex flex-wrap justify-center gap-3">
              {levelUpReward.coats.map((c) => (
                <div key={c.art_id} data-testid={`levelup-coat-${c.level}`} className="w-28">
                  <div className="mx-auto grid h-24 w-24 place-items-center overflow-hidden rounded-xl border border-emerald-200 bg-muted">
                    {c.art_image
                      ? <HorseArt src={c.art_image} color="#5a3e2b" size={88} />
                      : <div className="opacity-40 grayscale"><HorseSilhouette color="#9aa0a6" size={76} /></div>}
                  </div>
                  <p className="mt-1.5 truncate text-sm font-bold text-primary">{c.display_name}</p>
                  <p className="text-[11px] text-muted-foreground">{c.art_image ? "Look for this coat in your makeover picks!" : "Artwork coming soon"}</p>
                </div>
              ))}
            </div>
            <p className="mt-4 text-xs text-muted-foreground">These coats now have a chance to appear when you pick a new mustang for a makeover — they're never added to your stable automatically.</p>
            <button data-testid="levelup-reward-close" onClick={() => setLevelUpReward(null)} className="mt-4 h-11 w-full rounded-xl bg-emerald-600 font-bold text-white">Got it</button>
          </div>
        </div>
      )}

      {showSkip && save && save.status === "training" && (
        <SkipAhead day={save.day} horse={save.horse} summary={skipSummary} busy={busy}
          onConfirm={doSkip} onCancel={() => setShowSkip(false)}
          onContinue={() => { setShowSkip(false); setSkipSummary(null); setView("play"); }}
          onViewJournal={() => { setShowSkip(false); setSkipSummary(null); setView("journal"); }}
          onViewProfile={() => { setShowSkip(false); setSkipSummary(null); setView("profile"); }} />
      )}

      {stableComp && (
        <StableCompete competition={stableComp} horse={save?.horse} result={stableCompResult} busy={busy}
          onSubmit={submitStableCompete} onClose={closeStableCompete} />
      )}

      {player && (view === "leaderboard" || view === "player-profile" || view === "profile-edit" || view === "find-players" || view === "friends" || view === "barns" || view === "hma" || view === "shop" || view === "admin" || view === "forum" || view === "upgrades" || view === "premium" || view === "collectors" || view === "discipline-comp") && (
        <div className="fixed inset-0 z-[60] overflow-y-auto bg-background" data-testid="social-overlay">
          {view === "collectors"
            ? <CollectorsBook onBack={() => setView(save && save.status !== "selecting" ? "play" : "game-home")} />
            : view === "discipline-comp"
            ? <DisciplineCompetitions player={player} onBack={() => setView(save && save.status !== "selecting" ? "play" : "game-home")} onOpen={openAlumni} />
            : view === "forum"
            ? <Forum onBack={() => setView(save && save.status !== "selecting" ? "play" : "game-home")}
                onOpenProfile={(n) => { setProfileNum(n); setView("player-profile"); }} />
            : view === "leaderboard"
            ? <Leaderboard onBack={() => setView(save && save.status !== "selecting" ? "play" : "game-home")}
                onOpenProfile={(n) => { setProfileNum(n); setView("player-profile"); }} />
            : view === "upgrades"
            ? <StableUpgrades onBack={() => setView(save && save.status !== "selecting" ? "play" : "game-home")} onUpdate={(p) => setPlayer(p)} />
            : view === "premium"
            ? <PremiumShop onBack={() => setView(save && save.status !== "selecting" ? "play" : "game-home")}
                sessionParam={premiumReturn.session} cancelled={premiumReturn.cancelled}
                onClearReturn={() => setPremiumReturn({ session: null, cancelled: false })} />
            : view === "shop"
            ? <GameShop onBack={() => setView(save && save.status !== "selecting" ? "play" : "game-home")}
                keptHorses={(player?.alumni || []).filter((a) => a.decision === "keep").map((a) => ({ id: a.id, name: a.name, color_hex: a.color_hex, art_image: a.art_image }))}
                onUpdate={(p) => setPlayer(p)} onOpenUpgrades={() => setView("upgrades")} gameYear={player?.game_year} />
            : view === "admin" && player.role === "game_admin"
            ? <GameAdmin onBack={() => setView("game-home")} />
            : view === "profile-edit"
            ? <ProfileEditor player={player} onBack={() => { setProfileNum(player.player_number); setView("player-profile"); }}
                onSaved={(p) => { setPlayer(p); setProfileNum(player.player_number); setView("player-profile"); }} />
            : view === "friends"
            ? <FriendsPage onBack={() => setView(save && save.status !== "selecting" ? "play" : "game-home")}
                onOpenProfile={(n) => { setProfileNum(n); setView("player-profile"); }} />
            : view === "barns"
            ? <BarnsHub onBack={() => setView(save && save.status !== "selecting" ? "play" : "game-home")} />
            : view === "hma"
            ? <HMAPage onBack={() => setView(save && save.status !== "selecting" ? "play" : "game-home")} />
            : view === "find-players"
            ? <FindPlayers onBack={() => setView(save && save.status !== "selecting" ? "play" : "game-home")}
                onOpenProfile={(n) => { setProfileNum(n); setView("player-profile"); }} />
            : <PlayerProfileView playerNumber={profileNum}
                onBack={() => setView(save && save.status !== "selecting" ? "play" : "game-home")}
                onEdit={() => setView("profile-edit")}
                onOpenProfile={(n) => { setProfileNum(n); setView("player-profile"); }} />}
        </div>
      )}

      {player && (view === "tackroom" || view === "stall") && (
        <div className="fixed inset-0 z-[60] overflow-y-auto bg-background" data-testid="stable-overlay">
          {view === "tackroom"
            ? <TackRoom keptHorses={keptHorses} onBack={backToStable} onUpdate={setPlayer} />
            : <StallPage stall={stallSel} player={player} onBack={backToStable}
                onEnter={(al) => { setStallSel(null); openAlumni(al); }}
                onSell={(al) => { setStallSel(null); setView("stable"); setSellConfirm(al); }}
                onProfile={(al) => { setStallSel(null); openAlumni(al); }}
                onSetFavorite={setFavoriteHorse}
                onNewMakeover={() => { setStallSel(null); newMakeover(); }}
                onAssign={assignStall}
                onCustomize={() => { setStallSel(null); setView("shop"); }} />}
        </div>
      )}

      {sellConfirm && (
        <div className="fixed inset-0 z-[70] flex items-end justify-center bg-black/50 p-4 sm:items-center" data-testid="stable-sell-modal">
          <div className="w-full max-w-sm rounded-2xl border border-border bg-card p-5 shadow-xl">
            <h3 className="font-display text-xl font-bold text-primary">Sell {sellConfirm.name}?</h3>
            <p className="mt-1 text-sm text-muted-foreground">Fictional buyers will bid at auction. Extra training and competition wins raise the final price. This moves {sellConfirm.name} out of your stable.</p>
            <div className="mt-4 flex gap-2">
              <button data-testid="sell-cancel-btn" onClick={() => setSellConfirm(null)}
                className="h-11 flex-1 rounded-xl border-2 border-border font-bold text-primary active:scale-95">Keep</button>
              <button data-testid="sell-confirm-btn" disabled={busy} onClick={() => sellStable(sellConfirm)}
                className="h-11 flex-1 rounded-xl bg-secondary font-bold text-white disabled:opacity-60 active:scale-95">Sell at Auction</button>
            </div>
          </div>
        </div>
      )}

      {saleResult && (
        <div className="fixed inset-0 z-[86] flex items-center justify-center bg-black/60 p-4" data-testid="stable-sale-result" onClick={() => setSaleResult(null)}>
          <div className="w-full max-w-sm rounded-2xl border border-border bg-card p-4" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display text-xl font-bold text-primary">{saleResult.name} sold at auction!</h3>
            <AuctionReveal auction={saleResult.auction} coins={saleResult.coins} />
            <button data-testid="sale-result-close" onClick={() => setSaleResult(null)}
              className="mt-3 h-11 w-full rounded-xl bg-primary font-bold text-primary-foreground active:scale-95">Done</button>
          </div>
        </div>
      )}
      {player && <GamePopup />}
      <CoinHistory open={showCoinHistory} onClose={() => setShowCoinHistory(false)} />
    </AppLayout>
  );
}
